import { lazy } from "react";

import type { NavigationRoutingObject } from "@navigationRouting/types";
import { makeAdditionalSearchableFeature } from "@navigationRouting/utils";

import type { I18nT } from "@lib/dashboardI18n";

const PredictionsForNro = lazy(() =>
  import("@scenes/Predictions/PredictionsForNro").then((module) => ({
    default: module.PredictionsForNro,
  }))
);

export const predictionsNRO: NavigationRoutingObject = {
  Component: PredictionsForNro,
  noKnockoutFallback: true,
  componentFeatureFlippers: [],
  section: "analytics",
  // this is unused in navigation since we override it with specific predictive types
  heading: (t: I18nT<"dashboard-navigation">) => t("predictions", "Predictions"),
  pageNameForTracking: () => "Predictions",
  featureLimitationKeys: [
    "can_only_view_landing_page_predictive_events",
    "can_only_view_landing_page_predictive_churn",
    "can_only_view_landing_page_ai_item_recommendations",
  ],
  getDeveloperNavigationAllowed: (
    { predictionFeatures, hasPermission },
    { type }: { type?: "churn" | "purchase" | "recommendations" } = {}
  ) => {
    if (!hasPermission("appGroup", { permission: "viewPredictions" })) {
      return false;
    }

    switch (type) {
      case "churn":
        return predictionFeatures.churn.ENABLED;
      case "purchase":
        return predictionFeatures.purchase.ENABLED;
      case "recommendations":
        return predictionFeatures.recommendations.ENABLED;
      default:
        return (
          predictionFeatures.churn.ENABLED ||
          predictionFeatures.purchase.ENABLED ||
          predictionFeatures.recommendations.ENABLED
        );
    }
  },
  additionalSearchTerms: () => [],
  additionalSearchableFeatures: (t: I18nT<"dashboard-navigation">) => ({
    predictions: makeAdditionalSearchableFeature({
      heading: t("predictions", "Predictions"),
      pathname: "/engagement/predictions/predictions/:appGroupId",
      query: { predictionType: "predictions" },
      additionalSearchTerms: [
        {
          keywords: [
            t("predictions-alt-keyword-1", "churn prediction"),
            t("predictions-alt-keyword-2", "purchase prediction"),
            t("predictions-alt-keyword-3", "recommendations prediction"),
            t("predictions-alt-keyword-4", "event prediction"),
            t("predictions-alt-keyword-5", "predictive analytics"),
            t("predictions-alt-keyword-6", "item prediction"),
            t("predictions-alt-keyword-7", "item recommendation"),
          ],
        },
      ],
    }),
  }),
  defaultRoute: "/engagement/predictions/predictions/:appGroupId",
};
